import React from 'react';
import { motion } from 'framer-motion';
import { Sparkles, GitCompareArrows, Languages, Workflow, ShieldCheck, BarChart3 } from 'lucide-react';

const FEATURES = [
  {
    icon: Sparkles,
    title: 'Domain-tuned extraction',
    body: 'Trained on the specific shape of physical commodity confirmations — crude grades, refined products, LNG cargoes, GCC counterparties.',
  },
  {
    icon: GitCompareArrows,
    title: 'Confidence-scored fields',
    body: 'Every field comes with a confidence score. Above threshold: auto-fill. Below: flagged for review. Nothing posts without your eyes.',
  },
  {
    icon: Languages,
    title: 'Bilingual by default',
    body: 'English-Arabic mixed threads, transliterations, recorded-call transcripts. Built for how desks in Doha and Riyadh actually communicate.',
  },
  {
    icon: Workflow,
    title: 'Posts to your TRM',
    body: 'Allegro, Openlink Endur, RightAngle, Eka, or your internal system. Native connectors, not brittle screen-scraping.',
  },
  {
    icon: ShieldCheck,
    title: 'Full audit trail',
    body: 'Every field, every edit, every approval — timestamped and attributed. Built for compliance reviews and trade disputes.',
  },
  {
    icon: BarChart3,
    title: 'Desk-level analytics',
    body: 'Time-to-book, error rates, exposure by counterparty. Turn your booking workflow into a measurable, improvable process.',
  },
];

const FeaturesSection = () => {
  return (
    <section className="relative py-28 md:py-32 bg-canvas-black">
      <div className="container-prose">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0, y: 8 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.5 }}
            className="text-[11px] font-semibold tracking-[0.12em] uppercase text-amber-light mb-5"
          >
            What's inside
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="text-display font-bold text-canvas-white text-balance"
          >
            Purpose-built for the way
            <br className="hidden md:block" />{' '}
            <span className="text-ink-faint">commodity desks actually trade.</span>
          </motion.h2>
        </div>

        <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {FEATURES.map(({ icon: Icon, title, body }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.25 }}
              transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
              className="group p-7 rounded-2xl border border-white/[0.07] bg-gradient-to-b from-white/[0.02] to-transparent hover:border-amber/20 hover:from-amber/[0.04] transition-all duration-500"
            >
              <div className="w-10 h-10 rounded-xl bg-white/[0.04] ring-1 ring-white/[0.08] flex items-center justify-center mb-5 group-hover:bg-amber/10 group-hover:ring-amber/25 transition-colors">
                <Icon size={17} className="text-canvas-white group-hover:text-amber transition-colors" strokeWidth={1.6} />
              </div>
              <h3 className="text-[16.5px] font-semibold text-canvas-white tracking-tight mb-2.5">
                {title}
              </h3>
              <p className="text-[13.5px] leading-relaxed text-ink-faint">
                {body}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FeaturesSection;
